import React, { useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  Background,
  BackgroundVariant,
  Node,
  Edge,
  MarkerType,
} from '@xyflow/react';
import { Card, CardContent, Typography, Chip, Box, Divider, useTheme } from '@mui/material';
import { EquipmentNode, EquipData, SideHandles } from './EquipmentShapes';
import { NodeProps } from '@xyflow/react';
import { installPidLint } from './pidLint';
import { installPidHops } from './pidHop';
import { measureLaneTargets } from './pidAlign';

/**
 * P&ID 详细图通用渲染壳：新工艺路线只需提供设备/边界/管线数据（见准则第 4 节生成顺序）。
 * 布局约定：主线 y=233（handle 中线），塔顶走廊 y≈115，底部重组分车道 y≈620；
 * 设备 y = 233 - svgH/2 - labelH/2（labelH：两行标签 32 / 三行标签 45，把手中线按 div 中线算）。
 */

export interface PidDevice {
  id: string;
  kind: EquipData['kind'];
  x: number;
  y: number;
  tag: string;
  name: string;
  note?: string;
}

export interface PidPort {
  id: string;
  label: string;
  medium: string;
  x: number;
  y: number;
  direction: 'in' | 'out';
}

export interface PidPipe {
  id: string;
  from: string;
  fromSide: 'top' | 'bottom' | 'left' | 'right';
  to: string;
  toSide: 'top' | 'bottom' | 'left' | 'right';
  medium: string;
  label?: string;
}

export type PidMedia = Record<string, { label: string; light: string; dark: string; width: number; dash?: string }>;

interface PortData extends Record<string, unknown> {
  label: string;
  color: string;
  direction: 'in' | 'out';
}

const PortNode: React.FC<NodeProps> = ({ data }) => {
  const d = data as PortData;
  return (
    <div
      className="pid-label"
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: d.color,
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        position: 'relative',
      }}
    >
      {d.direction === 'in' ? '▶' : '◀'} {d.label}
      <SideHandles />
    </div>
  );
};

const nodeTypes = { equip: EquipmentNode, port: PortNode };

export interface PidAnnotation {
  title: string;
  items: string[];
}

export interface PidDiagramProps {
  routeName: string;
  title: string;
  subtitle: string;
  media: PidMedia;
  devices: PidDevice[];
  ports: PidPort[];
  pipes: PidPipe[];
  feedstock?: string[];
  products?: string[];
  annotations?: PidAnnotation[];
}

const PidInner: React.FC<PidDiagramProps> = ({ routeName, title, subtitle, media, devices, ports, pipes, feedstock, products, annotations }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const rfInstance = useReactFlow();
  // 车道吸附的目标 y（受控模式下必须写回数据源：setNodes 改 store 会被 props 回滚）
  const [snappedY, setSnappedY] = useState<Record<string, number>>({});

  useEffect(() => {
    installPidLint();
    installPidHops();
  }, []);

  useEffect(() => {
    const apply = () => {
      try {
        const t = measureLaneTargets(rfInstance);
        if (Object.keys(t).length > 0) setSnappedY((prev) => ({ ...prev, ...t }));
      } catch {
        /* 单次失败由后续调度补 */
      }
    };
    const t1 = setTimeout(apply, 120);
    // 字体加载后标签高度漂移 1~2px，二次收敛
    const t2 = setTimeout(apply, 900);
    const doc = document as Document & { fonts?: { ready: Promise<unknown> } };
    let t3: ReturnType<typeof setTimeout> | undefined;
    doc.fonts?.ready.then(() => {
      t3 = setTimeout(apply, 60);
    });
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      if (t3) clearTimeout(t3);
    };
  }, [rfInstance]);

  const { nodes, edges } = useMemo(() => {
    const colorOf = (m: string) => {
      const meta = media[m];
      return meta ? meta[isDark ? 'dark' : 'light'] : '#9e9e9e';
    };
    const ns: Node[] = [
      ...devices.map<Node>((d) => ({
        id: d.id,
        type: 'equip',
        position: { x: d.x, y: snappedY[d.id] ?? d.y },
        data: { kind: d.kind, tag: d.tag, name: d.name, note: d.note, dark: isDark } as EquipData,
        draggable: true,
      })),
      ...ports.map<Node>((p) => ({
        id: p.id,
        type: 'port',
        position: { x: p.x, y: snappedY[p.id] ?? p.y },
        data: { label: p.label, color: colorOf(p.medium), direction: p.direction } as PortData,
        draggable: true,
      })),
    ];
    const es: Edge[] = pipes
      .filter((p) => media[p.medium])
      .map((p) => {
        const m = media[p.medium];
        const color = m[isDark ? 'dark' : 'light'];
        return {
          id: p.id,
          source: p.from,
          sourceHandle: `${p.fromSide}-s`,
          target: p.to,
          targetHandle: `${p.toSide}-t`,
          type: 'smoothstep' as const,
          animated: m.dash === '12 5',
          label: p.label,
          labelStyle: { fill: isDark ? '#eceff1' : '#37474f', fontSize: 10, fontWeight: 600 },
          labelBgStyle: { fill: isDark ? '#1e1e1e' : '#ffffff' },
          style: { stroke: color, strokeWidth: m.width, strokeDasharray: m.dash },
          markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
        };
      });
    return { nodes: ns, edges: es };
  }, [isDark, media, devices, ports, pipes, snappedY]);

  return (
    <Card sx={{ backgroundColor: isDark ? '#1e1e1e' : '#ffffff' }}>
      <CardContent>
        <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#66bb6a' : '#2e7d32', fontWeight: 600 }}>
          {routeName} · {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {subtitle}
        </Typography>
        {(feedstock?.length || products?.length) && (
          <Box sx={{ mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {feedstock?.length ? (
              <Box>
                <Typography variant="caption" color="text.secondary">原料</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                  {feedstock.map((item) => (
                    <Chip key={item} label={item} size="small" color="primary" variant="outlined" />
                  ))}
                </Box>
              </Box>
            ) : null}
            {products?.length ? (
              <Box>
                <Typography variant="caption" color="text.secondary">产品 / 副产</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                  {products.map((item) => (
                    <Chip key={item} label={item} size="small" color="success" variant="outlined" />
                  ))}
                </Box>
              </Box>
            ) : null}
          </Box>
        )}
        <Divider sx={{ mb: 1.5, borderColor: isDark ? '#444444' : '#e0e0e0' }} />
        <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          {Object.keys(media).map((k) => (
            <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 26,
                  height: 0,
                  borderTopWidth: media[k].dash ? 2 : 3,
                  borderTopStyle: media[k].dash ? 'dashed' : 'solid',
                  borderTopColor: media[k][isDark ? 'dark' : 'light'],
                }}
              />
              <Typography variant="caption" sx={{ fontSize: 11 }}>
                {media[k].label}
              </Typography>
            </Box>
          ))}
        </Box>
        {annotations && annotations.length > 0 && (
          <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {annotations.map((a) => (
              <Box key={a.title}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                  {a.title}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {a.items.map((it, i) => (
                    <Typography
                      key={i}
                      component="li"
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'list-item', lineHeight: 1.6 }}
                    >
                      {it}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        <Box
          sx={{
            height: 640,
            border: '1px solid',
            borderColor: isDark ? '#444444' : '#e0e0e0',
            borderRadius: 2,
          }}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.08, maxZoom: 0.6 }}
            minZoom={0.05}
            maxZoom={2}
            zoomOnDoubleClick={false}
            proOptions={{ hideAttribution: true }}
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={16}
              size={1}
              color={isDark ? '#333333' : '#e0e0e0'}
            />
          </ReactFlow>
        </Box>
      </CardContent>
    </Card>
  );
};

const PidDiagramShell: React.FC<PidDiagramProps> = (props) => (
  <ReactFlowProvider>
    <PidInner {...props} />
  </ReactFlowProvider>
);

export default PidDiagramShell;
